import { useState, useEffect, useRef, useMemo } from "react";
import { TeamLogo } from "../../../../components/shared/team-logo";
import { ChevronDown } from "lucide-react";

const DEFAULT_PRIMARY = "#2563eb";
const DEFAULT_SECONDARY = "#3b82f6";

const fmt = (val, decimals) => (val != null ? Number(val).toFixed(decimals) : "—");

const StatItem = ({ label, value, suffix = "" }) => (
  <div className="flex flex-col items-center">
    <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
    <span className="font-semibold text-gray-900 tabular-nums">
      {value}
      {suffix}
    </span>
  </div>
);

export const TeamHeader = ({ teamData, onReady }) => {
  const teamColors = teamData?.team_colors || {};
  const primaryColor = teamColors.primary || DEFAULT_PRIMARY;
  const secondaryColor = teamColors.secondary || primaryColor || DEFAULT_SECONDARY;

  const readyRef = useRef(false);
  const dropdownRef = useRef(null);

  const years = useMemo(() => {
    const battingYears = teamData?.batting_stats?.map((s) => s.year) || [];
    const pitchingYears = teamData?.pitching_stats?.map((s) => s.year) || [];
    return [...new Set([...battingYears, ...pitchingYears])].sort((a, b) => b - a);
  }, [teamData]);

  const [selectedYear, setSelectedYear] = useState(years[0]);
  const [dropdown, setDropdown] = useState(false);

  useEffect(() => {
    if (years.length > 0 && !years.includes(selectedYear)) {
      setSelectedYear(years[0]);
    }
  }, [years, selectedYear]);

  useEffect(() => {
    if (!readyRef.current) {
      readyRef.current = true;
      onReady?.();
    }
  }, [onReady]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const batting = useMemo(
    () => teamData?.batting_stats?.find((s) => s.year === selectedYear) || {},
    [teamData, selectedYear]
  );
  const pitching = useMemo(
    () => teamData?.pitching_stats?.find((s) => s.year === selectedYear) || {},
    [teamData, selectedYear]
  );

  const ops = (Number(batting.ob_pct) || 0) + (Number(batting.slg_pct) || 0);

  return (
    <div className="h-full flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div
        className="relative h-20 transition-colors duration-500"
        style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
      >
        <div className="absolute left-1/2 -bottom-10 transform -translate-x-1/2">
          <div className="w-24 h-24 rounded-full overflow-hidden ring-4 ring-white shadow-lg bg-white flex items-center justify-center p-1.5">
            <TeamLogo teamId={teamData.team_id} teamName={teamData.team_name} size="w-full h-full" />
          </div>
        </div>
      </div>

      <div className="flex-1 pt-12 px-4 pb-4 flex flex-col">
        <div className="text-center mb-4">
          <h1 className="text-xl font-bold text-gray-800">{teamData.team_name}</h1>
          <p className="text-sm text-gray-500">
            {teamData.conference} · {teamData.division?.replace?.("ncaa_", "D") || teamData.division}
          </p>
          {teamData.coach && (
            <p className="text-xs text-gray-400 mt-0.5">Head Coach: {teamData.coach}</p>
          )}
        </div>

        <div className="relative mb-3" ref={dropdownRef}>
          <button
            onClick={() => setDropdown(!dropdown)}
            className="w-full flex items-center justify-between px-3 py-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
          >
            <span>{selectedYear} Season</span>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${dropdown ? "rotate-180" : ""}`} />
          </button>
          {dropdown && (
            <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {years.map((y) => (
                <button
                  key={y}
                  onClick={() => {
                    setSelectedYear(y);
                    setDropdown(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                    y === selectedYear ? "bg-blue-50 text-blue-700 font-semibold" : "hover:bg-gray-50 text-gray-700"
                  }`}
                >
                  {y}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 flex flex-col gap-3">
          <div className="rounded-lg border border-gray-100 p-3" style={{ background: `${primaryColor}08` }}>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Offense</div>
            <div className="grid grid-cols-4 gap-2">
              <StatItem label="WAR" value={fmt(batting.war, 1)} />
              <StatItem label="wRC+" value={fmt(batting.wrc_plus, 0)} />
              <StatItem label="OPS" value={fmt(ops, 3)} />
              <StatItem label="wOBA" value={fmt(batting.woba, 3)} />
            </div>
            <div className="grid grid-cols-4 gap-2 mt-2 pt-2 border-t border-gray-100">
              <StatItem label="AVG" value={fmt(batting.ba, 3)} />
              <StatItem label="OBP" value={fmt(batting.ob_pct, 3)} />
              <StatItem label="SLG" value={fmt(batting.slg_pct, 3)} />
              <StatItem label="HR" value={batting.hr ?? "—"} />
            </div>
          </div>

          <div className="rounded-lg border border-gray-100 p-3" style={{ background: `${secondaryColor}08` }}>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Pitching</div>
            <div className="grid grid-cols-4 gap-2">
              <StatItem label="WAR" value={fmt(pitching.war, 1)} />
              <StatItem label="ERA" value={fmt(pitching.era, 2)} />
              <StatItem label="RA9" value={fmt(pitching.ra9, 2)} />
              <StatItem label="WHIP" value={fmt(pitching.whip, 2)} />
            </div>
            <div className="grid grid-cols-4 gap-2 mt-2 pt-2 border-t border-gray-100">
              <StatItem label="K%" value={pitching.k_pct != null ? fmt(pitching.k_pct, 1) : "—"} suffix="%" />
              <StatItem label="BB%" value={pitching.bb_pct != null ? fmt(pitching.bb_pct, 1) : "—"} suffix="%" />
              <StatItem label="K/9" value={fmt(pitching.k9, 1)} />
              <StatItem label="K-BB%" value={fmt(pitching.k_minus_bb_pct, 1)} suffix="%" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
